import { motion } from "framer-motion";
import { ShieldCheck, FlaskConical, Syringe, HardHat, Fingerprint, ClipboardCheck } from "lucide-react";
import { RESOURCES } from "@/constants/testIds";
import { Link } from "react-router-dom";

const items = [
  {
    slug: "background",
    Icon: Fingerprint,
    title: "Comprehensive background checks",
    body: "Every driver clears a multi-state criminal background check and MVR review before they carry a single Waylio parcel.",
  },
  {
    slug: "drugscreen",
    Icon: FlaskConical,
    title: "Drug screening",
    body: "Pre-employment and random drug screens are standard across our roster — non-negotiable for anyone on route.",
  },
  {
    slug: "hipaa",
    Icon: Syringe,
    title: "HIPAA certification",
    body: "Drivers assigned to medical and pharmaceutical routes complete HIPAA privacy training and re-certify annually.",
  },
  {
    slug: "osha",
    Icon: HardHat,
    title: "OSHA safety training",
    body: "Bloodborne pathogen and hazard-communication training for anyone handling clinical, laboratory or biohazard packages.",
  },
  {
    slug: "insurance",
    Icon: ShieldCheck,
    title: "Fully insured & bonded",
    body: "Commercial auto, cargo, and general liability coverage on every route — with certificates available on request.",
  },
  {
    slug: "chain",
    Icon: ClipboardCheck,
    title: "Chain-of-custody logging",
    body: "Every package is scanned at pickup and drop-off with a photo, signature, or receipt sent by SMS or email.",
  },
];

export default function ResourcesPage() {
  return (
    <main className="pt-10 md:pt-12 pb-24 bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
          <div className="col-span-12 md:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">
              Why Clients Choose Us
            </div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-forest">
              The reasons your business is <span className="italic font-light">safe</span> with us.
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4 flex items-end">
            <p className="text-base md:text-lg text-charcoal/70">
              Trust is the whole product. Here's what every Waylio driver clears
              before they're on the road for you.
            </p>
          </div>
        </div>

        <section
          data-testid={RESOURCES.root}
          className="grid grid-cols-12 gap-4 md:gap-6"
        >
          {items.map((it, i) => (
            <motion.article
              key={it.slug}
              data-testid={RESOURCES.item(it.slug)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 md:col-span-6 lg:col-span-4 p-6 md:p-8 bg-cream border border-forest/15 hover:border-terracotta transition-colors"
            >
              <div className="flex items-center justify-between">
                <it.Icon className="w-6 h-6 text-forest" strokeWidth={1.5} />
                <span className="font-mono text-xs text-charcoal/40">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-6 font-serif text-2xl text-forest leading-tight">
                {it.title}
              </h3>
              <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">
                {it.body}
              </p>
            </motion.article>
          ))}
        </section>

        <div className="mt-16 md:mt-20 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8 bg-forest text-cream p-8 md:p-12">
            <h3 className="font-serif text-3xl md:text-5xl leading-tight">
              Talk to a real person — <span className="italic font-light text-terracotta">today</span>.
            </h3>
            <p className="mt-4 max-w-lg text-cream/70">
              Get a quote by phone or email, meet your assigned driver, and get
              proof of delivery in your inbox.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/quote"
                className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-cream px-5 py-3 text-sm font-medium"
              >
                Get a Quote
              </Link>
              <a
                href="mailto:support@waylio.com"
                className="inline-flex items-center gap-2 rounded-full border border-cream/30 text-cream px-5 py-3 text-sm font-medium hover:bg-cream hover:text-forest transition-colors"
              >
                support@waylio.com
              </a>
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 bg-cream p-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-charcoal/60">
              Documents
            </div>
            <p className="mt-3 text-forest font-serif text-xl leading-snug">
              Certificates of insurance, driver background summaries, and HIPAA
              attestations available upon request for partnership evaluations.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
